import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Building2,
  MapPin,
  Edit,
  Plus,
  Image
} from 'lucide-react'
import { Card, Button, Drawer, ImageUpload } from '../ui'
import { useAuthStore } from '../../stores/authStore'
import {
  organizationService,
  Organization,
  Address,
  CreateAddressRequest,
  UpdateAddressRequest
} from '../../services/organizationService'
import { useToast } from '../../contexts/ToastContext'
import AddressForm from './AddressForm'
import { PERMISSIONS, hasPermission } from '../../utils/permissions'
import { isSubscriptionsEnabled } from '../../utils/featureFlags'
import { getSubscriptionTierDisplayName } from '../../utils/subscriptionHelpers'

const OrganizationSettings: React.FC = () => {
  const { user, setOrganizationLogoUrl } = useAuthStore()
  const { showError, showSuccess } = useToast()
  const [loadingOrganization, setLoadingOrganization] = useState(true)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [isAddressDrawerOpen, setIsAddressDrawerOpen] = useState(false)
  const [billingAddress, setBillingAddress] = useState<Address | null>(null)
  const [loadingAddress, setLoadingAddress] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [deletingLogo, setDeletingLogo] = useState(false)

  const [organizationName, setOrganizationName] = useState(
    user?.organizationName || ''
  )

  // Check if user has permission to manage billing
  const canManageSettings = hasPermission(
    user,
    PERMISSIONS.MANAGE_ORGANIZATION_BILLING
  )

  // Fetch billing address
  const fetchBillingAddress = async () => {
    if (!user?.organizationId) return

    try {
      setLoadingAddress(true)
      const response = await organizationService.getAddresses(
        user.organizationId,
        true
      )

      if (response.success && response.data && response.data.length > 0) {
        setBillingAddress(response.data[0])
      } else {
        setBillingAddress(null)
      }
    } catch (error) {
      console.error('Error fetching billing address:', error)
      setBillingAddress(null)
    } finally {
      setLoadingAddress(false)
    }
  }

  // Fetch organization data on mount
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!user?.organizationId) {
        setLoadingOrganization(false)
        return
      }

      try {
        setLoadingOrganization(true)

        // Fetch organization details
        const orgResponse = await organizationService.getOrganization(
          user.organizationId
        )

        if (orgResponse.success && orgResponse.data) {
          setOrganization(orgResponse.data)
          setOrganizationName(orgResponse.data.organizationName)

          // Sync thumbnail logo URL to auth store for sidebar display
          setOrganizationLogoUrl(orgResponse.data.logoUrlThumbnail || null)

          // Fetch billing address if user has permission
          if (canManageSettings) {
            fetchBillingAddress()
          }
        } else {
          showError(
            'Failed to load organization',
            'Could not fetch organization details'
          )
        }
      } catch (error) {
        console.error('Error fetching organization:', error)
        showError(
          'Load Failed',
          error instanceof Error
            ? error.message
            : 'Failed to load organization data'
        )
      } finally {
        setLoadingOrganization(false)
      }
    }

    fetchOrganizationData()
  }, [user?.organizationId, showError, canManageSettings])

  const handleSaveAddress = async (
    data: CreateAddressRequest | UpdateAddressRequest
  ) => {
    if (!user?.organizationId) return

    try {
      setSavingAddress(true)

      let response
      if (billingAddress) {
        // Update existing address
        response = await organizationService.updateAddress(
          user.organizationId,
          billingAddress.id,
          data as UpdateAddressRequest
        )
      } else {
        // Create new address
        response = await organizationService.createAddress(
          user.organizationId,
          data as CreateAddressRequest
        )
      }

      if (response.success) {
        showSuccess(
          billingAddress ? 'Address Updated' : 'Address Added',
          `Billing address has been successfully ${
            billingAddress ? 'updated' : 'added'
          }.`
        )
        setIsAddressDrawerOpen(false)
        await fetchBillingAddress()
      } else {
        showError(
          'Save Failed',
          response.message ||
            `Failed to ${billingAddress ? 'update' : 'add'} address`
        )
      }
    } catch (error) {
      console.error('Error saving address:', error)
      showError(
        'Operation Failed',
        error instanceof Error ? error.message : 'Failed to save address'
      )
    } finally {
      setSavingAddress(false)
    }
  }

  const handleLogoUpload = async (file: File) => {
    if (!user?.organizationId) return

    try {
      setUploadingLogo(true)
      const response = await organizationService.uploadLogo(
        user.organizationId,
        file
      )

      if (response.success && response.data) {
        showSuccess(
          'Logo Updated',
          'Your organization logo has been uploaded successfully.'
        )
        // Refresh organization data to get all logo URL variants
        const orgResponse = await organizationService.getOrganization(
          user.organizationId
        )
        if (orgResponse.success && orgResponse.data) {
          setOrganization(orgResponse.data)
          // Update the auth store with thumbnail URL for sidebar display
          setOrganizationLogoUrl(orgResponse.data.logoUrlThumbnail || null)
        }
      } else {
        showError(
          'Upload Failed',
          response.error?.message || 'Failed to upload logo'
        )
      }
    } catch (error) {
      showError(
        'Upload Failed',
        error instanceof Error ? error.message : 'An error occurred'
      )
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleLogoRemove = async () => {
    if (!user?.organizationId) return

    try {
      setDeletingLogo(true)
      const response = await organizationService.deleteLogo(user.organizationId)

      if (response.success) {
        showSuccess('Logo Removed', 'Your organization logo has been removed.')
        // Clear the logo URL in auth store for sidebar display
        setOrganizationLogoUrl(null)
        setOrganization((prev) =>
          prev
            ? {
                ...prev,
                logoUrlDisplay: null,
                logoUrlThumbnail: null,
                logoUrlOriginal: null
              }
            : null
        )
      } else {
        showError(
          'Remove Failed',
          response.error?.message || 'Failed to remove logo'
        )
      }
    } catch (error) {
      showError(
        'Remove Failed',
        error instanceof Error ? error.message : 'An error occurred'
      )
    } finally {
      setDeletingLogo(false)
    }
  }


  // Show loading state while fetching organization
  if (loadingOrganization) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className='space-y-6'
      >
        <Card variant='outlined' padding='lg'>
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary'></div>
          </div>
        </Card>
      </motion.div>
    )
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        role='tabpanel'
        id='organization-panel'
        aria-labelledby='organization-tab'
        className='space-y-6'
      >
        {/* Organization Header Card */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='px-5 sm:px-6 py-4'>
            <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4'>
              <div className='flex items-start gap-3'>
                <div className='p-2 bg-blue-50 rounded-lg border border-blue-100'>
                  <Building2 className='h-5 w-5 text-blue-600' />
                </div>
                <div className='flex-1'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1'>
                    Organization
                  </p>
                  <h2 className='text-xl lg:text-2xl font-bold text-gray-900 tracking-tight'>
                    {String(organizationName || 'Loading...')}
                  </h2>

                  {/* Subscription Tier - Show only when subscriptions feature is disabled */}
                  {!isSubscriptionsEnabled() &&
                    organization?.subscriptionTier && (
                      <div className='flex items-center gap-2 mt-2'>
                        <span className='text-sm text-gray-600'>Plan:</span>
                        <span className='inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200'>
                          {getSubscriptionTierDisplayName(
                            organization.subscriptionTier
                          )}
                        </span>
                      </div>
                    )}

                  {organization?.createdAt && (
                    <div className='flex items-center gap-1.5 text-xs text-gray-500 mt-1.5'>
                      <svg
                        className='h-3.5 w-3.5'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                        />
                      </svg>
                      <span>
                        Member since{' '}
                        {(() => {
                          try {
                            return new Date(
                              organization.createdAt
                            ).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          } catch (error) {
                            return new Date(
                              organization.createdAt
                            ).toLocaleDateString()
                          }
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Logo Section - Permission gated */}
        {canManageSettings && (
          <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
            <div className='px-6 sm:px-8 py-6'>
              <div className='mb-6'>
                <div className='flex items-center gap-2 mb-1'>
                  <Image className='h-5 w-5 text-gray-600' />
                  <h3 className='text-sm font-semibold text-gray-900'>
                    Organization Logo
                  </h3>
                </div>
                <p className='text-sm text-gray-600'>
                  Upload your organization's logo to customize the application
                  branding
                </p>
              </div>

              <ImageUpload
                currentImageUrl={organization?.logoUrlDisplay}
                onUpload={handleLogoUpload}
                onRemove={
                  organization?.logoUrlDisplay ? handleLogoRemove : undefined
                }
                accept='image/png,image/jpeg,image/webp'
                maxSizeMB={2}
                helpText='PNG, JPG, or WebP. Max 2MB. Recommended: 200x200px or larger.'
                isUploading={uploadingLogo}
                isDeleting={deletingLogo}
                disabled={uploadingLogo || deletingLogo}
                enableCrop={true}
                cropAspectRatio={1}
              />
            </div>
          </div>
        )}

        {/* Social Media / Billing Card */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='px-6 sm:px-8 py-6'>
            {/* Billing Address Section - Only show if user has permission */}
            {canManageSettings && (
              <>
                <div className='mt-8 mb-6'>
                  <h3 className='text-sm font-semibold text-gray-900 mb-1'>
                    Billing Address
                  </h3>
                  <p className='text-sm text-gray-600'>
                    Manage your organization's billing address
                  </p>
                </div>

                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-center gap-4'>
                    <div className='p-2.5 bg-white rounded-lg border border-gray-200'>
                      <MapPin className='h-5 w-5 text-gray-600' />
                    </div>
                    <div className='flex-1'>
                      {loadingAddress ? (
                        <div className='flex items-center gap-2'>
                          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600'></div>
                          <p className='text-sm text-gray-600'>
                            Loading address...
                          </p>
                        </div>
                      ) : billingAddress ? (
                        <div>
                          <p className='text-sm font-medium text-gray-900'>
                            {billingAddress.addressLine1}
                          </p>
                          {billingAddress.addressLine2 && (
                            <p className='text-xs text-gray-600 mt-0.5'>
                              {billingAddress.addressLine2}
                            </p>
                          )}
                          <p className='text-xs text-gray-600 mt-0.5'>
                            {billingAddress.locality}, {billingAddress.region}{' '}
                            {billingAddress.postalCode}
                          </p>
                          <p className='text-xs text-gray-500 mt-0.5'>
                            {billingAddress.countryCode}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className='text-sm font-medium text-gray-900'>
                            No billing address
                          </p>
                          <p className='text-xs text-gray-500 mt-0.5'>
                            Add your billing address for invoices
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant='primary'
                    onClick={() => setIsAddressDrawerOpen(true)}
                    disabled={loadingAddress}
                    size='xs'
                  >
                    {billingAddress ? (
                      <>
                        <Edit className='h-4 w-4 mr-2' />
                        Edit Address
                      </>
                    ) : (
                      <>
                        <Plus className='h-4 w-4 mr-2' />
                        Add Address
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Billing Address Drawer */}
      {canManageSettings && (
        <Drawer
          isOpen={isAddressDrawerOpen}
          onClose={() => setIsAddressDrawerOpen(false)}
          title={
            billingAddress ? 'Edit Billing Address' : 'Add Billing Address'
          }
          description='Update your organization billing address for invoices and payments'
          icon={<MapPin className='h-4 w-4' />}
        >
          {user?.organizationId && (
            <AddressForm
              address={billingAddress}
              organizationId={user.organizationId}
              onSave={handleSaveAddress}
              onCancel={() => setIsAddressDrawerOpen(false)}
              isSaving={savingAddress}
            />
          )}
        </Drawer>
      )}
    </>
  )
}

export default OrganizationSettings
